import { create } from 'zustand';
import { WEBSOCKET_URL } from '../config';

// Zustand store for real-time activity data (active users and mounts)
// This allows components to subscribe to WebSocket data without prop drilling
const useActivityStore = create((set, get) => ({
  // Active users and mounts from WebSocket
  activeUsers: [],
  activeMounts: [],

  // WebSocket connection status
  usersConnected: false,
  mountsConnected: false,

  // WebSocket references (stored outside Zustand for management)
  userWebSocket: null,
  mountWebSocket: null,

  // Actions
  setActiveUsers: (users) => set({ activeUsers: users }),
  setActiveMounts: (mounts) => set({ activeMounts: mounts }),
  setUsersConnected: (connected) => set({ usersConnected: connected }),
  setMountsConnected: (connected) => set({ mountsConnected: connected }),

  // Initialize WebSocket connections
  initializeWebSockets: () => {
    const state = get();

    // Connect to users WebSocket
    const connectUserWebSocket = () => {
      const ws = new WebSocket(WEBSOCKET_URL + '/ws/users/');

      ws.onopen = () => {
        console.log('Users WebSocket connected');
        set({ usersConnected: true });
      };

      ws.onclose = () => {
        console.log('Users WebSocket disconnected, reconnecting...');
        set({ usersConnected: false });
        setTimeout(connectUserWebSocket, 1000);
      };

      ws.onerror = (error) => {
        console.error('Users WebSocket error:', error);
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          set({ activeUsers: data });
        } catch (error) {
          console.error('Error parsing user WebSocket data:', error);
        }
      };

      set({ userWebSocket: ws });
    };

    // Connect to mounts WebSocket
    const connectMountWebSocket = () => {
      const ws = new WebSocket(WEBSOCKET_URL + '/ws/mounts/');

      ws.onopen = () => {
        console.log('Mounts WebSocket connected');
        set({ mountsConnected: true });
      };

      ws.onclose = () => {
        console.log('Mounts WebSocket disconnected, reconnecting...');
        set({ mountsConnected: false });
        setTimeout(connectMountWebSocket, 1000);
      };

      ws.onerror = (error) => {
        console.error('Mounts WebSocket error:', error);
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          set({ activeMounts: data });
        } catch (error) {
          console.error('Error parsing mount WebSocket data:', error);
        }
      };

      set({ mountWebSocket: ws });
    };

    connectUserWebSocket();
    connectMountWebSocket();
  },

  // Cleanup WebSocket connections
  cleanupWebSockets: () => {
    const state = get();
    if (state.userWebSocket) {
      state.userWebSocket.close();
    }
    if (state.mountWebSocket) {
      state.mountWebSocket.close();
    }
    set({
      userWebSocket: null,
      mountWebSocket: null,
      usersConnected: false,
      mountsConnected: false
    });
  },

  // Helper functions to check if specific items are active
  isUserActive: (username) => {
    const state = get();
    return state.activeUsers.some(user => user.USER === username);
  },

  isMountActive: (mountpoint) => {
    const state = get();
    return state.activeMounts.some(mount => mount.name === mountpoint && mount.connected);
  },

  getUserConnectionCount: (username) => {
    const state = get();
    return state.activeUsers.filter(user => user.USER === username).length;
  },

  getMountConnectionCount: (mountpoint) => {
    const state = get();
    const mount = state.activeMounts.find(m => m.name === mountpoint);
    return mount ? mount.clients : 0;
  }
}));

export default useActivityStore;
